/**
 * Registration API Client
 * Handles API calls for user registration flow
 */

import { apiRequestPublic } from '../utils/axios'
import type {
  TokenValidationResponse,
  RegistrationRequest,
  RegistrationResponse,
  TokenStatusResponse,
} from '../types/registration'

/**
 * Validate a registration token before displaying the form
 *
 * @param token - Registration token from email link
 * @returns Token validation data (email, role, expiration)
 * @throws ApiClientError if token is invalid or expired
 */
export async function validateToken(token: string): Promise<TokenValidationResponse> {
  try {
    const response = await apiRequestPublic('POST', '/auth/validate-token', {
      token,
    })

    // API wraps the payload as { success, data: { email, role, expires_at } }
    const data = response?.data ?? response

    return {
      valid: response?.success ?? true,
      email: data?.email,
      role: data?.role,
      expiresAt: data?.expires_at,
    }
  } catch (error: any) {
    console.error('❌ Token validation failed:', error)
    throw error
  }
}

/**
 * Complete user registration with password and profile information
 *
 * @param data - Registration data (token, password, firstName, lastName)
 * @returns Registration success response
 * @throws ApiClientError if registration fails
 */
export async function completeRegistration(data: RegistrationRequest): Promise<RegistrationResponse> {
  try {
    const response = await apiRequestPublic('POST', '/auth/complete-registration', {
      token: data.token,
      password: data.password,
      firstName: data.firstName,
      lastName: data.lastName,
    })

    const result = response?.data ?? response

    console.log('✅ Registration completed successfully:', result?.user?.email)

    return {
      message: result?.message,
      userId: result?.user?.id,
      email: result?.user?.email,
    }
  } catch (error: any) {
    console.error('❌ Registration failed:', error)
    throw error
  }
}

/**
 * Check token status without side effects (optional)
 *
 * @param token - Registration token to check
 * @returns Token status data
 * @throws ApiClientError if token is invalid
 */
export async function checkTokenStatus(token: string): Promise<TokenStatusResponse> {
  try {
    const response = await apiRequestPublic('GET', `/auth/token-status/${token}`)

    const data = response?.data ?? response

    return {
      valid: data?.status === 'valid',
      email: data?.email,
      expiresAt: data?.expires_at,
    }
  } catch (error: any) {
    console.error('❌ Token status check failed:', error)
    throw error
  }
}

/**
 * Registration API service object
 */
export const registrationApi = {
  validateToken,
  completeRegistration,
  checkTokenStatus,
}
